<template>
  <div id="app">
    <AppGrid />

    <template v-if="game.questionsIndex !== null">
      <transition
        @enter="enterSection"
        @leave="leaveSection"
        mode="out-in"
        appear
      >
        <Intro
          class="app__intro"
          data-anim="intro"
          v-if="game.questionsIndex === -1"
          :key="game.questionsIndex || -1"
        />

        <main
          class="app__game game"
          :data-anim="game.gameEnded ? 'results' : 'sections'"
          v-else
          :key="game.questionsIndex"
        >
          <GameSection />
        </main>
      </transition>
    </template>

    <BgIllustration v-if="!inApp"/>

    <GameControls />

    <AppAudio />

    <GameIllustration />

    <GameCountDown />

    <GameResult />

    <AppLogo />

    <AppCollectionLink :campaignUrl="campaignUrl" />

    <AppCursor />

    <RotateDevice />

    <AppGrain />

    <AppCookiebar />

    <AppDisclaimer />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import debounce from 'lodash/debounce';
import { utm, build } from 'url-utm-params';

import AppDisclaimer from '@/components/AppDisclaimer.vue';
import AppCookiebar from '@/components/AppCookiebar.vue';
import AppGrid from '@/components/AppGrid.vue';
import AppCollectionLink from '@/components/AppCollectionLink.vue';
import AppLogo from '@/components/AppLogo.vue';
import AppGrain from '@/components/AppGrain.vue';
import BgIllustration from '@/components/BgIllustration.vue';
import Intro from '@/components/Intro.vue';
import GameSection from '@/components/GameSection.vue';
import GameControls from '@/components/GameControls.vue';
import GameIllustration from '@/components/GameIllustration.vue';
import GameCountDown from '@/components/GameCountDown.vue';
import GameResult from '@/components/GameResult.vue';

import transitions from '@/scripts/animations/transitions';
import load from '@/scripts/loader';
import RotateDevice from '@/components/RotateDevice.vue';
import AppAudio from '@/components/AppAudio.vue';
import AppCursor from '@/components/AppCursor.vue';
import { inApp } from '@/scripts/utils';
import audio from '@/scripts/audio';
import apollo from '@/scripts/apollo';


export default {
  name: 'App',

  components: {
    AppDisclaimer,
    AppCookiebar,
    AppGrid,
    AppCollectionLink,
    AppLogo,
    AppGrain,
    AppAudio,
    AppCursor,
    BgIllustration,
    Intro,
    GameSection,
    GameControls,
    GameIllustration,
    GameCountDown,
    GameResult,
    RotateDevice,
  },

  data() {
    return {
      inApp: inApp(),
    };
  },

  computed: {
    ...mapState({
      game: 'currentGame',
    }),
    campaignUrl() {
      const link = 'https://www.gucci.com/it/it/st/capsule/circular-line-off-the-grid';
      const defaults = inApp ? {
        utm_campaign: 'offthegrid_catalog',
        utm_content: 'offthegrid_catalog',
        utm_medium: 'app',
        utm_source: 'offthegrid_gucciapp',
        utm_term: 'offthegrid_catalog',
      } : {
        utm_campaign: 'offthegrid_catalog',
        utm_content: 'offthegrid_catalog',
        utm_medium: 'catalog',
        utm_source: 'offthegrid_catalog',
        utm_term: 'offthegrid_catalog',
      };
      const parsed = utm(document.location.href);

      return build(link, { ...defaults, ...parsed }, false);
    },
  },

  mounted() {
    apollo.init(this.$el.querySelector('.cursor__element'));
    load().then(() => {
      this.$store.state.currentGame.questionsIndex = -1;
      audio.init();
    });
    this.resize();
    window.addEventListener('resize', debounce(() => this.resize(), 200));
  },

  methods: {
    resize() {
      this.$eventHub.$emit('windowResize');
      const rw = Math.max(window.innerWidth / 1920, 0.7);
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
      document.documentElement.style.setProperty('--unit-vh', `${window.innerHeight / 9}px`);
      document.documentElement.style.setProperty('--rw', rw);
    },

    enterSection(el, done) {
      const timelines = el.dataset.anim;

      transitions.get(`${timelines}-enter`)(el);
      if (timelines === 'sections') setTimeout(() => this.$eventHub.$emit('count-down-start'), 1450);
      if (!this.game.showProgress && timelines !== 'intro' && timelines !== 'results') this.game.showProgress = true;

      done();
    },
    leaveSection(el, done) {
      const timelines = el.dataset.anim;

      transitions.get(`${timelines}-leave`)(el).finished.then(done);
    },
  },
};
</script>

<style lang="scss">
@import './scss/base';
@import './scss/fonts';

:root {
  --unit-vw: calc(100vw / 16);

  @include mq($until: ipadP) {
    --unit-vw: calc(100vw / 5);
  }
}

html,
body {
  touch-action: none;
}

body.is-loading {
  cursor: wait !important;
}

#app {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  text-align: center;
  background-color: $color-bg;

  @include grid-game();
}

.app__intro {
  position: relative;
  grid-column: 1 / -1;
}

.app__game {
  position: relative;
  grid-column: 1 / -1;

  display: flex;
  align-items: stretch;
  justify-content: stretch;

  @include mq(ipadP) {
    grid-column: 1 / 11;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity .5s cubic-bezier(.5,0,0,1);
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.paragraph__line {
  white-space: nowrap;
  width: 100%;
}

.grid-hide {
  display: block;
  position: absolute;
  top: 0px;
  left: 0px;
  width: 100%;
  height: 100%;

  background-color: $color-bg;

  pointer-events: none;
}

.underline__blue,
.underline__red,
.underline__yellow,
.underline__black {
  position: relative;

  &::after {
    content: ' ';
    position: absolute;
    bottom: 0px;
    left: 0px;
    width: 100%;
    height: 10px;
    background-position: center center;
    background-size: 100% auto;
    background-repeat: no-repeat;

    transition: clip-path .6s ease;
    clip-path: inset(0% 100% 0% 0%);
  }

  @at-root [data-animate-lines] &::after {
    clip-path: inset(0% 0% 0% 0%);
  }
}

.underline__blue {
  font-weight: inherit;

  &::after {
    background-image: url('/img/svg/underline-blue.svg');
  }
}
.underline__yellow {
  font-weight: inherit;

  &::after {
    background-image: url('/img/svg/underline-yellow.svg');
  }
}
.underline__red {
  font-weight: inherit;

  &::after {
    background-image: url('/img/svg/underline-red.svg');
  }
}

.underline__black {
  font-weight: inherit;

  &::after {
    top: 0px;
    bottom: auto;
    height: 100%;
    background-image: url('/img/svg/underline-black.svg');
  }
}
</style>
